import { z } from 'zod';

/**
 * Named tax rates, and the country packs that seed them.
 *
 * A rate is more than a percentage: two 0% rows can need different sentences on
 * the invoice, and a VAT return groups by what KIND of rate a line used. That is
 * what `category` carries.
 */
export const TaxRateCategorySchema = z.enum([
  'standard',
  'reduced',
  'zero',
  'exempt',
  'reverse_charge',
  'small_business',
]);
export type TaxRateCategory = z.infer<typeof TaxRateCategorySchema>;

export interface TaxRate {
  id: string;
  country: string;
  code: string;
  nameEn: string;
  nameAr: string;
  /** Percent, e.g. "19.0000". */
  rate: string;
  category: TaxRateCategory;
  /** The sentence to print when this rate is used; null for an ordinary rate. */
  noteEn: string | null;
  noteAr: string | null;
  isDefault: boolean;
  sortOrder: number;
  isActive: boolean;
}

export const TaxRateUpdateSchema = z.object({
  nameEn: z.string().min(1).max(120).optional(),
  nameAr: z.string().min(1).max(120).optional(),
  rate: z.string().regex(/^\d{1,3}(\.\d{1,4})?$/).optional(),
  category: TaxRateCategorySchema.optional(),
  noteEn: z.string().max(300).nullable().optional(),
  noteAr: z.string().max(300).nullable().optional(),
  isDefault: z.boolean().optional(),
  sortOrder: z.number().int().optional(),
  isActive: z.boolean().optional(),
});
export type TaxRateUpdate = z.infer<typeof TaxRateUpdateSchema>;

/** One row of the per-rate breakdown a multi-rate invoice must print. */
export interface TaxBreakdownRow {
  /** The percentage as stored on the lines, e.g. "19.0000". */
  rate: string;
  /** Registry name when the rate is a known one, else null. */
  label: string | null;
  category: TaxRateCategory | null;
  /** Net (excl. tax) of the lines at this rate. */
  net: string;
  /** Tax charged on them. */
  tax: string;
  /** The legal sentence for this rate, when it has one. */
  note: string | null;
}

/** A seed row for a country pack. `rate` is a plain number here for legibility. */
export interface TaxRateSeed {
  code: string;
  nameEn: string;
  nameAr: string;
  rate: number;
  category: TaxRateCategory;
  noteEn?: string;
  noteAr?: string;
  isDefault?: boolean;
}

/**
 * GERMANY. The two headline rates are long-standing and safe to encode; the
 * zero-rated rows exist because a German invoice may not simply print "0%" —
 * §14 Abs. 4 Nr. 8 UStG requires the REASON, and these are the three reasons a
 * small trader actually meets.
 */
export const TAX_PACK_DE: TaxRateSeed[] = [
  {
    code: 'DE_STD', nameEn: 'VAT 19% (standard)', nameAr: 'ضريبة القيمة المضافة ١٩٪ (الأساسي)',
    rate: 19, category: 'standard', isDefault: true,
  },
  {
    // The one that matters for a stationer: books and printed matter are 7%,
    // so a single stationery invoice legitimately carries both rates.
    code: 'DE_RED', nameEn: 'VAT 7% (reduced)', nameAr: 'ضريبة القيمة المضافة ٧٪ (المخفض)',
    rate: 7, category: 'reduced',
  },
  {
    code: 'DE_ZERO', nameEn: 'Tax free (§4 UStG)', nameAr: 'معفاة من الضريبة (§4 UStG)',
    rate: 0, category: 'exempt',
    noteEn: 'Steuerfreie Lieferung gemäß § 4 UStG.',
    noteAr: 'Steuerfreie Lieferung gemäß § 4 UStG.',
  },
  {
    code: 'DE_RC', nameEn: 'Reverse charge (§13b UStG)', nameAr: 'الاحتساب العكسي (§13b UStG)',
    rate: 0, category: 'reverse_charge',
    noteEn: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge).',
    noteAr: 'Steuerschuldnerschaft des Leistungsempfängers (Reverse Charge).',
  },
  {
    code: 'DE_KU', nameEn: 'Small business (§19 UStG)', nameAr: 'منشأة صغيرة (§19 UStG)',
    rate: 0, category: 'small_business',
    noteEn: 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.',
    noteAr: 'Gemäß § 19 UStG wird keine Umsatzsteuer berechnet.',
  },
];

/**
 * IRAQ. Deliberately NOT a table of invented percentages: Iraq has no general
 * VAT on goods, and the sales taxes that do exist attach to particular services
 * and change by decree. So the pack seeds the shape — a zero-rated default a
 * stationer can invoice on all day, plus one clearly-named row for a shop that
 * IS charging a sales tax — and leaves the number to the shop, which is the only
 * party who knows what it owes.
 */
export const TAX_PACK_IQ: TaxRateSeed[] = [
  {
    code: 'IQ_NONE', nameEn: 'No tax', nameAr: 'بدون ضريبة',
    rate: 0, category: 'zero', isDefault: true,
  },
  {
    code: 'IQ_SALES', nameEn: 'Sales tax (set your rate)', nameAr: 'ضريبة المبيعات (حدد النسبة)',
    rate: 0, category: 'standard',
  },
];

export const TAX_PACKS: Record<string, TaxRateSeed[]> = { DE: TAX_PACK_DE, IQ: TAX_PACK_IQ };

/** Module key → the country it seeds. */
export const TAX_PACK_MODULES: Record<string, string> = { tax_de: 'DE', tax_iq: 'IQ' };
